// «يوم الريّس»: the page's signature. REFERENCE.md §8.
//
// Desktop with motion: a tall track holds a stage pinned to the screen. The
// scrollbar decides WHICH chapter is on; the change itself runs on its own clock
// (photo 1.1s, text .35s later), so nothing but the rail's fill moves with the
// scroll inside a chapter.
//
// Below 1024, and under reduced motion, the same markup is a stack of cards
// (sticky on a phone, plain under reduced motion). `pinned` picks the layout, in
// one place, so the layout and the scroll logic can never disagree.
//
// LENGTH. The reference runs 384svh over six chapters, 64svh each. We have five,
// so the same 384svh is shared: the section stays its measured 4356px at 1440x900
// and each chapter gets 76.8svh. More chapters than six would each keep 64svh.
// This one rule lives in Pillars.css as --_run.
//
// PHOTO LAYERS. Scrolling fast crosses boundaries faster than a photo can fade
// in. Every photo that is on stays on, stacked in the order it came on, until the
// one on top has finished; only then do the ones beneath it drop away unseen. A
// chapter reached again while it is still in the stack is uncovered by fading out
// what lies above it. So at any speed, in either direction, no photo ever jumps.
//
// ANCHORS. `#day-<id>` must stay true however far the page has scrolled, and a
// stuck element's position is not its place on the page. So the ids live on plain
// markers: in the track at the chapter's own offset when pinned, and just before
// each card when stacked.

import { useEffect, useLayoutEffect, useReducer, useRef } from "react";
import CtaLink from "./CtaLink";
import { ArrowLeft, Check } from "lucide-react";
import { DAY, HOW, signupHref } from "../Home/content";
import SectionHead from "./SectionHead";
import { PILLARS } from "./copy";
import { Photo } from "./media";
import usePinnedSteps from "./motion/usePinnedSteps";
import "./Pillars.css";

// Which photograph tells each chapter (Landing.js says why).
const CHAPTER_PHOTOS = { jobs: "install", techs: "rooftop", stock: "stock", money: "money", night: "night" };
const PHOTO_ORDER = Object.values(CHAPTER_PHOTOS);
// Anchored low, so the top of the frame is what goes. install: its bright duct
// lining would sit behind the head's link. rooftop: a manufacturer's emblem in its
// top 15%. stock: a hanging store banner with a retailer's logo, ending 44% of the
// way down. The last two are BRAND.md §7, and they hold at any window shape.
const CHAPTER_CROPS = {
    jobs: { "--ld-focal": "50% 100%", "--ld-zoom": 1.12 },
    techs: { "--ld-focal": "50% 100%", "--ld-zoom": 1.18 },
    stock: { "--ld-focal": "50% 100%", "--ld-zoom": 1.78 },
};

// How wide a photo is really drawn. A pinned one fills the stage, which can be
// taller than the window is wide; a stacked one fills a card about as wide as the
// window. The numbers scale that for a crop's zoom, stopping at 1.55 because the
// next file up is heavy for very little under the overlay. Stacked, only stock's
// crop is deep enough to need more pixels.
const PINNED_RES = { jobs: 1.12, techs: 1.18, stock: 1.55 };
const sizesFor = (pinned, id) => {
    if (!pinned) return id === "stock" ? "155vw" : "100vw";
    const z = PINNED_RES[id] || 1;
    return `max(${Math.round(100 * z)}vw, ${Math.round(178 * z)}vh)`;
};

const pad = (n) => String(n).padStart(2, "0");

// The stack, bottom first. Each entry is { i, leaving }.
function layersReducer(stack, action) {
    const at = stack.findIndex((l) => l.i === action.i);
    if (action.type === "show") {
        if (at === -1) return [...stack, { i: action.i, leaving: false }];
        return stack.map((l, k) => {
            if (k === at) return l.leaving ? { ...l, leaving: false } : l;
            if (k > at) return l.leaving ? l : { ...l, leaving: true };
            return l;
        });
    }
    if (at === -1) return stack;
    // Solid now, so whatever lies beneath can no longer be seen.
    if (action.type === "covered") return !stack[at].leaving && at > 0 ? stack.slice(at) : stack;
    // Faded right out on its way off the stack.
    if (action.type === "gone") return stack[at].leaving ? stack.filter((_, k) => k !== at) : stack;
    return stack;
}

function useChapterPhotos(active) {
    const [layers, dispatch] = useReducer(layersReducer, active, (i) => [{ i, leaving: false }]);
    // Before paint, so the change of photo and of text start in the same frame.
    useLayoutEffect(() => {
        dispatch({ type: "show", i: active });
    }, [active]);

    const stateOf = (i) => {
        const at = layers.findIndex((l) => l.i === i);
        if (at === -1) return { className: "", z: undefined };
        return { className: layers[at].leaving ? "is-leaving" : "is-on", z: at + 1 };
    };
    // Trust what is on screen, not which transition just ended: scrolling to and
    // fro on a boundary, a fade-out can end just as the photo is asked back, and
    // treating that as "solid" would pull away the photo beneath it.
    const onFadeEnd = (e, i) => {
        if (e.target !== e.currentTarget || e.propertyName !== "opacity") return;
        const opacity = parseFloat(getComputedStyle(e.currentTarget).opacity);
        if (opacity > 0.99) dispatch({ type: "covered", i });
        else if (opacity < 0.01) dispatch({ type: "gone", i });
    };
    return { stateOf, onFadeEnd };
}

// A photo decoded ahead of time fades in whole. Left to the browser, it can pop in
// halfway through its fade on a busy machine. Each is decoded as it arrives, and
// the chapters either side again at every change, since a browser can drop them.
function usePredecodedPhotos(rootRef, pinned, active) {
    useEffect(() => {
        const root = rootRef.current;
        if (!root || !pinned) return undefined;
        const decode = (img) => { img?.decode?.().catch(() => {}); };
        const onLoad = (e) => { if (e.target.tagName === "IMG") decode(e.target); };
        root.addEventListener("load", onLoad, true);
        return () => root.removeEventListener("load", onLoad, true);
    }, [rootRef, pinned]);

    useEffect(() => {
        const root = rootRef.current;
        if (!root || !pinned) return;
        const imgs = root.querySelectorAll(".ld-pillars__photo img");
        [imgs[active - 1], imgs[active + 1]].forEach((img) => {
            if (img?.complete) img.decode?.().catch(() => {});
        });
    }, [rootRef, pinned, active]);
}

function Chapter({ item, i, count, pinned, active, photos, cta }) {
    const isActive = pinned && i === active;
    const layer = photos.stateOf(i);
    const photoName = CHAPTER_PHOTOS[item.id] || PHOTO_ORDER[i % PHOTO_ORDER.length];
    const crop = CHAPTER_CROPS[item.id];
    const points = (item.points || []).filter(Boolean);

    return (
        <li className="ld-pillars__item">
            {pinned ? null : (
                <span className="ld-pillars__anchor" id={`day-${item.id}`} style={{ "--i": i }} />
            )}
            <article
                className={`ld-pillars__slide ${isActive ? "is-active" : ""}`.trim()}
                style={{ "--i": i }}
                inert={pinned && !isActive}
            >
                <div
                    className={`ld-pillars__photo ${pinned ? layer.className : ""}`.trim()}
                    style={pinned && layer.z ? { zIndex: layer.z } : undefined}
                    onTransitionEnd={(e) => photos.onFadeEnd(e, i)}
                >
                    <Photo
                        name={photoName}
                        sizes={sizesFor(pinned, item.id)}
                        style={crop}
                    />
                </div>
                <div className="ld-container ld-pillars__text">
                    <p className="ld-pillars__count">
                        <span className="ld-pillars__num" dir="ltr">{pad(i + 1)} / {pad(count)}</span>
                        {item.module ? <span>{item.module}</span> : null}
                    </p>
                    <h3 className="ld-pillars__title">{item.title}</h3>
                    <p className="ld-pillars__body">{item.body}</p>
                    <CtaLink className="ld-pillars__more" href={cta?.href || signupHref()}>
                        <span>{cta?.text || HOW.ctaText}</span>
                        <ArrowLeft aria-hidden="true" />
                    </CtaLink>
                    {pinned && points.length ? (
                        <ul className="ld-pillars__points">
                            {points.map((point) => (
                                <li key={point}>
                                    <Check aria-hidden="true" />
                                    <span>{point}</span>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </div>
            </article>
        </li>
    );
}

export default function Pillars({ day, planNote, cta }) {
    const track = useRef(null);
    const stage = useRef(null);
    const items = day?.items?.length ? day.items : DAY.items;
    const count = items.length;
    const { active, pinned, goTo } = usePinnedSteps(track, { stickyRef: stage, count });
    const photos = useChapterPhotos(active);
    usePredecodedPhotos(stage, pinned, active);

    return (
        <section
            className={`ld-section ld-section--night ld-pillars ${pinned ? "ld-pillars--pinned" : "ld-pillars--stacked"}`}
            id="day"
            aria-labelledby="ld-pillars-title"
            style={{ "--_n": count }}
        >
            <div className="ld-decor" aria-hidden="true">
                <div className="ld-grain" />
            </div>
            <div className="ld-pillars__track" ref={track}>
                {pinned ? items.map((item, i) => (
                    <span className="ld-pillars__marker" id={`day-${item.id}`} key={item.id} style={{ "--i": i }} />
                )) : null}
                <div className="ld-pillars__stage" ref={stage}>
                    <div className="ld-container ld-pillars__head">
                        <SectionHead
                            id="ld-pillars-title"
                            title={day?.title || DAY.title}
                            lead={day?.subtitle || DAY.subtitle}
                            flat={pinned}
                            aside={<a className="ld-ghost" href="#inside">{PILLARS.asideLabel}</a>}
                        />
                        {planNote ? (
                            <p className="ld-small ld-pillars__note" data-ld="rise" style={{ "--ld-d": ".35s" }}>{planNote}</p>
                        ) : null}
                    </div>
                    <ol className="ld-pillars__slides">
                        {items.map((item, i) => (
                            <Chapter
                                key={item.id}
                                item={item}
                                i={i}
                                count={count}
                                pinned={pinned}
                                active={active}
                                photos={photos}
                                cta={cta}
                            />
                        ))}
                    </ol>
                    {pinned && count > 1 ? (
                        <nav className="ld-container ld-pillars__rail" aria-label={PILLARS.railLabel}>
                            <ol>
                                {items.map((item, i) => (
                                    <li key={item.id}>
                                        <button
                                            type="button"
                                            className="ld-pillars__tick"
                                            style={{ "--i": i }}
                                            onClick={() => goTo(i)}
                                            aria-label={item.module || item.title}
                                            aria-current={i === active ? "step" : undefined}
                                        />
                                    </li>
                                ))}
                            </ol>
                        </nav>
                    ) : null}
                </div>
            </div>
        </section>
    );
}
